import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import OnboardingWelcome from "./OnboardingWelcome";
import OnboardingPlayers from "./OnboardingPlayers";
import OnboardingData from "./OnboardingData";
import OnboardingComplete from "./OnboardingComplete";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { ROUTES } from '@/lib/routes';

type OnboardingStep = "welcome" | "players" | "data" | "complete";

export function OnboardingWizard() {
  const { showOnboarding, loading: onboardingLoading, completeOnboarding } = useOnboarding();
  const { pathname } = useLocation();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("welcome");
  const [playersAdded, setPlayersAdded] = useState(0);
  const [dataUploaded, setDataUploaded] = useState<string[]>([]);
  const { translate: t } = useTranslation();

  // Suppress legacy wizard on the dedicated Club Setup Assistant route
  const isClubSetup = pathname === ROUTES.CLUB_SETUP;

  const steps: OnboardingStep[] = ["welcome", "players", "data", "complete"];
  const currentStepIndex = steps.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleComplete = async () => {
    await completeOnboarding();
  };

  // Don't render anything until onboarding status is known — prevents flicker
  // Also suppress on the dedicated Club Setup Assistant route
  if (onboardingLoading || !showOnboarding || isClubSetup) return null;

  return (
    <Dialog open={showOnboarding} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto p-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Progress Bar */}
        <div className="sticky top-0 z-10 bg-card border-b px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium text-muted-foreground">
              {t("onboarding_step_of", { current: String(currentStepIndex + 1), total: String(steps.length) })}
            </h2>
            <span className="text-sm font-medium">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <div className="px-6 py-8 md:px-12 md:py-12">
          {currentStep === "welcome" && (
            <OnboardingWelcome onNext={handleNext} onSkip={handleComplete} />
          )}
          
          {currentStep === "players" && (
            <OnboardingPlayers
              onNext={handleNext}
              onBack={handleBack}
              playersAdded={playersAdded}
              setPlayersAdded={setPlayersAdded}
            />
          )}
          
          {currentStep === "data" && (
            <OnboardingData
              onNext={handleNext}
              onBack={handleBack}
              dataUploaded={dataUploaded}
              setDataUploaded={setDataUploaded}
            />
          )}
          
          {currentStep === "complete" && (
            <OnboardingComplete
              onComplete={handleComplete}
              onBack={handleBack}
              playersAdded={playersAdded}
              dataUploaded={dataUploaded}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
